import { SchemeService } from "../scheme/scheme.service";
import { LinkBuilder } from "../link-builder.service";
import { KeycloakService } from "../keycloak.service";
import { OrganisationService } from "../organisation/organisation.service";

/**
* A service that provides user based operations
*/
export class UserService {
  public schemes: Array<InterACT.Interfaces.Schemes.Scheme> = [];
  public permissions: any;
  public userDetails: any;
  public selectedScheme: InterACT.Interfaces.Schemes.Scheme;
  public selectedSchemes: Array<InterACT.Interfaces.Schemes.Scheme>;
  private schemeObservers: Array<Function>;
  private userResource: InterACT.Interfaces.IResource<InterACT.Interfaces.Users.UserResponse>;
  private schemesStorageLabel = 'Actora-selectedSchemes';

  constructor(
    private schemeService: SchemeService,
    $resource: ng.resource.IResourceService,
    private localStorageService: ng.local.storage.ILocalStorageService,
    private linkBuilder: LinkBuilder,
    private $q: ng.IQService,
    private keycloakService: KeycloakService,
    private organisationService: OrganisationService
  ) {
    this.userResource = $resource('api/users/:userId');
    this.schemeObservers = [];
    this.selectedSchemes = [];
  }

  /**
   * A method that retrieves the user details for the user with the supplied userId
   * and then grabs all relevent information.
   * @param authResponse the auth response object we get back
   * @returns a promise that resolves with a service response object that contans the requested user details
   */
  public setUserProfile() {
    const deferred = this.$q.defer();
    const accessTokenParts = this.keycloakService.getCurrentToken().split('.');

    if (accessTokenParts.length < 3) {
      throw new Error('Cannot get OrganisationId from token. Token is not in the correct format.');
    }

    const decocdedJSONString = atob(accessTokenParts[1]);
    const accessTokenDetails = JSON.parse(decocdedJSONString);

    this.userDetails = <any>{
      id: undefined,
      attributes: undefined,
      username: accessTokenDetails.preferred_username,
      firstname: accessTokenDetails.given_name,
      lastName: accessTokenDetails.family_name,
      email: accessTokenDetails.email
    };
    this.permissions = this.setUserPermissions(this.keycloakService.getCurrentRealmAccess().roles);
    this.setUserSchemes().then(() => {
      deferred.resolve({});
    });

    return deferred.promise;
  }

  /**
   * Adds a scheme to the selected schemes list and the local storage
   *
   * @param schemeToSet scheme object we wish to add
   */
  public addSelectedScheme = (schemeToSet: InterACT.Interfaces.Schemes.Scheme) => {
    const schemeAlreadyActive = _.find(this.selectedSchemes, (scheme) => {
      return scheme.id === schemeToSet.id;
    });

    if (!schemeAlreadyActive) {
      this.selectedSchemes.push(schemeToSet);
    }

    this.selectedScheme = this.selectedSchemes[0];
    this.setStoredSchemes();
    this.notifySchemeObservers();
  }

  /**
   * Removes a scheme from the selected schemes list and the local storage
   *
   * @param schemeToRemove scheme object we wish to remove
   */
  public removeSelectedScheme = (schemeToRemove: InterACT.Interfaces.Schemes.Scheme) => {
    _.remove(this.selectedSchemes, (scheme) => {
      return scheme.id === schemeToRemove.id;
    });

    this.selectedScheme = this.selectedSchemes[0];
    this.setStoredSchemes();
    this.notifySchemeObservers();
  }

  /**
   * Clears the value of selectedScheme by setting to undefined.
   */
  public clearSelectedSchemes = () => {
    this.selectedSchemes = [];
    this.selectedScheme = undefined;
  }

  /**
   * Used to register callbacks for when scheme changes
   * @param callback the callback to set
   */
  public registerSchemeCallback = (callback: Function) => {
    this.schemeObservers.push(callback);
    return callback;
  }

  public unregisterCallback = (callback: Function) => {
    const indexToUnregister = this.schemeObservers.indexOf(callback);
    this.schemeObservers.splice(indexToUnregister, 1);
  };

  /**
   * Returns a scheme link from a scheme entered
   *
   * @param scheme the scheme to get the link for
   * @returns the produced scheme link
   */
  public convertSchemeToLink = (scheme: InterACT.Interfaces.Schemes.Scheme): InterACT.Interfaces.Users.SchemeLink => {
    return {
      abbreviation: scheme.reference,
      link: this.linkBuilder.getSchemesLink(scheme.id)
    };
  }

  /**
   * Determines the user permission set based on the roles assigned to the user from Keycloak
   *
   * @param roles roles that have been assigned to the user in Keycloak
   * @returns a permission set containing boolean flags for operations the user can carry out in the application
   */
  public setUserPermissions = (roles: Array<string>) => {
    const permissionSet = {
      interimPaymentRuleTemplates: { canRead: false },
      interimPaymentCalculationRequests: {
        canCreate: false,
        canRead: false
      },
      ui: {
      }
    };
    let parsedScope;
    let resourceKey;
    let uiArea;
    let action;

    //scopes follow naming convention of <resource>_<operation>, e.g. catalogues_read, financial-periods_create
    roles.forEach((scope) => {
      parsedScope = scope.split('_');
      if (parsedScope.length === 2) {
        resourceKey = _.camelCase(parsedScope[0]);
        action = _.capitalize(parsedScope[1]);

        if (permissionSet[resourceKey]) {
          permissionSet[resourceKey][`can${action}`] = true;
        } else {
          permissionSet[resourceKey] = {};
          permissionSet[resourceKey][`can${action}`] = true;
        }
      } else if (parsedScope.length === 3 && parsedScope[0] === 'ui') {
        uiArea = parsedScope[1];
        action = _.capitalize(parsedScope[2]);

        if (permissionSet.ui[uiArea]) {
          permissionSet.ui[uiArea]['can' + action] = true;
        } else {
          permissionSet.ui[uiArea] = {};
          permissionSet.ui[uiArea][`can${action}`] = true;
        }
      } else {
        console.log('Scope retrieved is not in expected format of <resource>_<operation>: ' + scope);
      }
    });

    return permissionSet;
  };

  /**
   * convenience function to parse the organisation name assigned to the user
   *
   * @param profile the users profile as supplied by Keycloak
   * @returns the organisation name
   */
  public getUserOrganisation() {
    const deferred = this.$q.defer();
    const accessTokenParts = this.keycloakService.getCurrentToken().split('.');

    if (accessTokenParts.length < 3) {
      throw new Error('Cannot get OrganisationId from token. Token is not in the correct format.');
    }

    const decocdedJSONString = atob(accessTokenParts[1]);
    const accessTokenDetails = JSON.parse(decocdedJSONString);
    let organisationId = accessTokenDetails.organisation.link.split('/').pop();

    this.organisationService.getSingleOrganisation(organisationId).then((response) => {
      deferred.resolve(response.organisation.name);
    });

    return deferred.promise;
  }

  /**
   * Sets the selected schemes on the local storage service
   */
  private setStoredSchemes() {
    this.localStorageService.set(this.schemesStorageLabel, { schemes: this.selectedSchemes });
  }

  /**
   * Sets the selected schemes on the local storage service
   */
  private getStoredSchemes() {
    const storage = this.localStorageService.get<InterACT.Interfaces.SchemeStorage>(this.schemesStorageLabel);
    return storage && storage.schemes ? storage.schemes : undefined;
  }

  /**
   * A method that retrieves a list of schemes for this user and sets them on this service
   */
  private setUserSchemes() {
    return this.schemeService.getList().then((response) => {
      this.schemes = response;
      this.setSelectedSchemes();
    });
  }

  /**
   * Adds multiple schemes to the selected schemes and the local storage
   */
  private addMultipleSchemes(schemes: Array<InterACT.Interfaces.Schemes.Scheme>) {
    this.selectedSchemes = schemes;
    this.selectedScheme = this.selectedSchemes[0];
    this.setStoredSchemes();
    this.notifySchemeObservers();
  }

  /**
   * A method that attempts to retrieve a saved scheme from the local storage service and uses
   * this value to set the currently selected scheme for the users session. If no scheme has been
   * saved previously, the first available from their list of visible schemes is used.
   */
  private setSelectedSchemes = () => {
    const storedSchemes = this.getStoredSchemes();
    this.clearSelectedSchemes();

    if (!storedSchemes) {
      this.addSelectedScheme(this.schemes[0]);
    } else {
      const validSchemes = _.filter(this.schemes, (scheme) => {
        return _.find(storedSchemes, (storedScheme) => {
          return scheme.id === storedScheme.id;
        });
      });

      if (validSchemes.length >= 1) {
        this.addMultipleSchemes(validSchemes);
      } else {
        this.addSelectedScheme(this.schemes[0]);
      }
    }
  }

  /**
   * Notifies all observers of scheme change by invoking their callbacks
   */
  private notifySchemeObservers() {
    this.schemeObservers.forEach((observerCallback) => {
      observerCallback();
    });
  }
}